import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';

import { DateTimeInput, RadioInput, Select } from '../index';
import { Skeleton } from './Skeleton';
import { Button } from '@lanl-web/lanl-components';

import {
  getLANLWeatherTowersQuery,
  getLANLWeatherDataQuery,
  getLANLWeatherVariablesQuery,
} from '../../utils/getWeatherData';

import './RawDataTable.scss';

const intervalOptions = [
  { label: '15-Minutes', value: '15', id: '15-minutes' },
  { label: '24-Hours', value: '24', id: '24-hours' },
];

const columnOptions = [
  {
    label: 'All Variables',
    value: 'all-columns',
    id: 'all-columns',
    defaultInterval: 'all-columns',
  },
  {
    label: 'Choose Variables',
    value: 'choose-columns',
    id: 'choose-columns',
    defaultInterval: 'all-columns',
  },
];

const formatDate = (date, isStart) => {
  const mDate = moment(date, 'YYYY-MM-DD', 'America/Denver');
  return isStart
    ? mDate.startOf('day').format('YYYY/MM/DD')
    : mDate.endOf('day').format('YYYY/MM/DD');
};

const alwaysInclude = ['DATETIME', 'DOY'];

export const RawDataTable = ({ className }) => {
  const classes = classNames('wm-raw-data-table', className);
  const [allVariables, setAllVariables] = useState([]);
  const [towerList, setTowerList] = useState([]);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [duration, setDuration] = useState('15');
  const [stationData, setStationData] = useState({});
  const [showColumnOptions, setShowColumnOptions] = useState(false);
  const [loadingVariables, setLoadingVariables] = useState(false);

  const getDefaultInterval = (options) => {
    return options?.length === 1 && options?.includes('24') ? '24' : duration;
  };

  useEffect(() => {
    (async () => {
      const variables = await getLANLWeatherVariablesQuery(setLoadingVariables);
      setAllVariables(variables ?? []);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const stations = await getLANLWeatherTowersQuery();
      setTowerList(stations);
      const defaultStation = stations?.find(({ value }) => value === 'ta6');
      setStationData(defaultStation || {});
    })();
  }, []);

  useEffect(() => {
    const options = stationData?.options ?? [];
    if (stationData && options) {
      setDuration(getDefaultInterval(stationData?.options ?? []));
    }
  }, [stationData]);

  const getTowerVariables = () =>
    allVariables.filter(
      ({ tower }) => tower === `${stationData.value}_${duration}`,
    );

  const stationIsActive = () => stationData.stationStatus === 'A';

  const handleSubmit = async (e) => {
    e.preventDefault();
    const target = e.target;
    const selectedDuration = target.interval.value;
    const indexValue = stationData.options.findIndex(
      (option) => option === selectedDuration,
    );
    const view = stationData.views[indexValue];
    setError('');

    const columns = !showColumnOptions
      ? 'DATETIME,DOY,*'
      : alwaysInclude
          .concat(
            getTowerVariables()
              .map(({ variable_name }) => {
                const isChecked = target[variable_name]?.checked;
                return isChecked && !alwaysInclude.includes(variable_name)
                  ? variable_name
                  : '';
              })
              .filter((val) => val),
          )
          .toString();

    const start = target.start.value;
    const end = target.end.value;
    const isBefore = moment(start)
      .startOf('day')
      .isBefore(moment(end).endOf('day'), 'hour');

    if (isBefore) {
      setProcessing(true);
      try {
        await getLANLWeatherDataQuery(
          columns,
          formatDate(end, false),
          'url',
          formatDate(start, true),
          setProcessing,
          view,
          setError,
        );
      } catch (err) {
        setError(err);
      }
    } else {
      setError(
        'End date must be greater than or equal to start date, please update your request and try again.',
      );
    }
  };

  const handleStationChange = (e) => {
    const selectedValue = e.target.value;
    const selectedStation = towerList.find(
      ({ value }) => value === selectedValue,
    );
    if (selectedStation) {
      setStationData(selectedStation);
    }
  };

  const toggleColumns = (e, value) => {
    setShowColumnOptions(value === 'choose-columns');
  };

  const handleIntervalToggle = (e, value) => {
    setDuration(value);
  };

  return (
    <div className={classes}>
      {towerList && towerList.length > 0 ? (
        <form className='raw-data-form' onSubmit={handleSubmit}>
          <Select
            startValue={stationData}
            label='Select A Station'
            id='station'
            options={towerList}
            required
            className='half-width lanl-form-group'
            onChange={handleStationChange}
          />
          <RadioInput
            label='Interval'
            groupName='interval'
            options={intervalOptions.map((option) => {
              const options = stationData?.options ?? [];
              const defaultInterval = getDefaultInterval(options);
              return {
                ...option,
                disabled: !options.includes(option.value),
                defaultInterval: defaultInterval,
              };
            })}
            className='half-width lanl-form-group'
            onChange={handleIntervalToggle}
          />
          <div className='half-width lanl-form-group'>
            <DateTimeInput
              label='Start*'
              id='start'
              required
              defaultValue={
                stationIsActive()
                  ? moment().format('YYYY-MM-DD')
                  : moment(stationData.maxDate, 'M/D/YYYY').format('YYYY-MM-DD')
              }
              min={moment(stationData.minDate, 'M/D/YYYY').format('YYYY-MM-DD')}
              max={
                stationIsActive()
                  ? moment().format('YYYY-MM-DD')
                  : moment(stationData.maxDate, 'M/D/YYYY').format('YYYY-MM-DD')
              }
            />
            <p style={{ color: 'darkgrey', fontSize: '1.1rem' }}>
              <small>Available from {stationData.minDate}</small>
            </p>
          </div>

          <div className='half-width lanl-form-group'>
            <DateTimeInput
              label='End*'
              id='end'
              required
              defaultValue={
                stationIsActive()
                  ? moment().format('YYYY-MM-DD')
                  : moment(stationData.maxDate, 'M/D/YYYY').format('YYYY-MM-DD')
              }
              min={moment(stationData.minDate, 'M/D/YYYY').format('YYYY-MM-DD')}
              max={
                stationIsActive()
                  ? moment().format('YYYY-MM-DD')
                  : moment(stationData.maxDate, 'M/D/YYYY').format('YYYY-MM-DD')
              }
            />
            {!stationIsActive() && (
              <p style={{ color: 'darkgrey', fontSize: '1.1rem' }}>
                <small>Available to {stationData.maxDate}</small>
              </p>
            )}
          </div>

          <RadioInput
            label=''
            groupName='columns'
            options={columnOptions.map((option) => ({
              ...option,
              disabled: false,
            }))}
            className='full-width lanl-form-group'
            onChange={toggleColumns}
          />
          {showColumnOptions && (
            <div className='table-variables'>
              {loadingVariables ? (
                <h5 className='text-center'>
                  Loading Tower Variables, one moment please
                </h5>
              ) : (
                <ul>
                  {getTowerVariables().map(
                    ({
                      variable_name,
                      variable_description,
                      variable_unit,
                    }) => (
                      <li className='variable' key={variable_name}>
                        <input
                          type='checkbox'
                          id={variable_name}
                          name={variable_name}
                          value={variable_name}
                        />
                        <label htmlFor={variable_name}>
                          {variable_name}
                          <small>
                            <em>({variable_unit})</em>
                          </small>
                          : {variable_description}
                        </label>
                      </li>
                    ),
                  )}
                </ul>
              )}
            </div>
          )}
          {error && <p className='error'>{error}</p>}
          <Button type='submit' outline arrow={false} disabled={processing}>
            {!processing ? 'Download Data' : 'Processing'}
          </Button>
        </form>
      ) : (
        <Skeleton />
      )}

      <p className='disclaimer'>
        If you encounter issues downloading more than 5 years of 15 minute data,
        reduce the time interval and download in smaller increments. If the
        problem persists, please contact weather@lanl.gov.
      </p>
    </div>
  );
};

RawDataTable.propTypes = {
  className: PropTypes.string,
};

export default RawDataTable;
